import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
const DIRS = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" };
const BACK_KEYS = new Set(["GoBack", "BrowserBack", "Escape", "Backspace"]);
const BACK_CODES = new Set([461, 10009]);

const NON_TEXT = new Set(["checkbox", "radio", "range", "button", "submit", "reset", "file", "color"]);
const isTyping = (el) =>
  el && ((el.tagName === "INPUT" && !NON_TEXT.has(el.type)) || ["TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable);

const visible = (el) => {
  if (el.tabIndex < 0 || el.closest("[aria-hidden='true']")) return false;
  const r = el.getBoundingClientRect();
  const cs = getComputedStyle(el);
  return r.width > 0 && r.height > 0 && cs.visibility !== "hidden" && cs.opacity !== "0";
};

const score = (from, to, dir) => {
  const fx = from.left + from.width / 2, fy = from.top + from.height / 2;
  const tx = to.left + to.width / 2, ty = to.top + to.height / 2;
  const primary = { up: from.top - to.bottom, down: to.top - from.bottom, left: from.left - to.right, right: to.left - from.right }[dir];
  const ahead = { up: ty < fy - 1, down: ty > fy + 1, left: tx < fx - 1, right: tx > fx + 1 }[dir];
  if (!ahead || primary < -from.height / 2) return Infinity;
  if ((dir === "left" || dir === "right") && (to.bottom <= from.top || to.top >= from.bottom)) return Infinity;
  const secondary = dir === "up" || dir === "down" ? Math.abs(tx - fx) : Math.abs(ty - fy) * 2;
  return Math.max(primary, 0) + secondary;
};

const move = (dir) => {
  const cur = document.activeElement;
  const items = [...document.querySelectorAll(FOCUSABLE)].filter((el) => el !== cur && visible(el));
  let next;
  if (!cur || cur === document.body) {
    next = items.find((el) => el.getBoundingClientRect().top >= 60) || items[0];
  } else {
    const from = cur.getBoundingClientRect();
    let best = Infinity;
    items.forEach((el) => {
      const s = score(from, el.getBoundingClientRect(), dir);
      if (s < best) { best = s; next = el; }
    });
  }
  if (!next) return;
  next.focus({ preventScroll: true });
  next.scrollIntoView({ block: "center", behavior: "smooth", inline: "center" });
};

const lastFocus = {};

const useFocusRestore = () => {
  const { pathname, search } = useLocation();
  const key = pathname + search;

  useEffect(() => {
    const save = (e) => {
      const id = e.target.getAttribute && e.target.getAttribute("data-testid");
      if (!id) return;
      const all = [...document.querySelectorAll(`[data-testid="${CSS.escape(id)}"]`)];
      lastFocus[key] = { id, n: all.indexOf(e.target), total: all.length };
    };
    document.addEventListener("focusin", save);
    const saved = lastFocus[key];
    let tries = 0;
    const timer = saved && setInterval(() => {
      const all = document.querySelectorAll(`[data-testid="${CSS.escape(saved.id)}"]`);
      const el = all.length >= saved.total || tries >= 12 ? all[saved.n] || all[0] : null;
      if (el && document.activeElement === document.body) {
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: "center", inline: "center" });
      }
      if (el || ++tries > 20) clearInterval(timer);
    }, 250);
    return () => {
      document.removeEventListener("focusin", save);
      if (timer) clearInterval(timer);
    };
  }, [key]);
};

export const TvNavigation = () => {
  const navigate = useNavigate();
  useFocusRestore();

  useEffect(() => {
    const onKey = (e) => {
      const active = document.activeElement;
      if (active?.closest?.("[role='listbox'], [role='menu']")) return;
      if (isTyping(active)) {
        const back = e.key === "Escape" || e.key === "GoBack" || e.key === "BrowserBack" || BACK_CODES.has(e.keyCode);
        const leaveInput = active.tagName === "INPUT" && (e.key === "ArrowUp" || e.key === "ArrowDown");
        if (back) {
          e.preventDefault();
          active.blur();
        } else if (leaveInput) {
          e.preventDefault();
          move(DIRS[e.key]);
        }
        return;
      }
      if (DIRS[e.key]) {
        e.preventDefault();
        move(DIRS[e.key]);
      } else if (BACK_KEYS.has(e.key) || BACK_CODES.has(e.keyCode)) {
        if (document.querySelector("[role='dialog']")) return;
        e.preventDefault();
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate("/");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return null;
};
